import { useEffect, useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';

/** CampaignController::sampleTemplate. */
const CSV_TEMPLATE = 'name,email,phone\nJane Doe,jane@example.com,0712345678\nJohn Kamau,john@example.com,0798765432\n';

export type AddCreatorInput =
  | { mode: 'single'; email: string; phone: string; name: string }
  | { mode: 'csv'; csv: string; fileName: string };

interface AddCreatorModalProps {
  open: boolean;
  saving: boolean;
  error: string;
  onClose: () => void;
  onAdd: (input: AddCreatorInput) => void;
}

const inputClass =
  'h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none';

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([CSV_TEMPLATE], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'creators-template.csv';
  link.click();
  URL.revokeObjectURL(url);
}

/** The live portal's Add creator page (one by email) and CSV upload, as one dialog. */
export default function AddCreatorModal({ open, saving, error, onClose, onAdd }: AddCreatorModalProps) {
  const [mode, setMode] = useState<'single' | 'csv'>('single');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    if (!open) return;
    setMode('single');
    setEmail('');
    setPhone('');
    setName('');
    setCsv('');
    setFileName('');
    setFileError('');
  }, [open]);

  const pickFile = (file: File | undefined) => {
    setFileError('');
    setCsv('');
    setFileName('');
    if (!file) return;
    if (!/\.(csv|txt)$/i.test(file.name)) {
      setFileError('Upload a .csv file.');
      return;
    }
    file
      .text()
      .then((content) => {
        setCsv(content);
        setFileName(file.name);
      })
      .catch(() => setFileError('Could not read that file.'));
  };

  const valid = mode === 'single' ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) : csv.trim().length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon="ri-user-add-line"
      title="Add creators"
      subtitle="Each creator gets the campaign invite by email and SMS"
      widthClassName="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="ri-user-add-line"
            disabled={!valid || saving}
            onClick={() =>
              onAdd(mode === 'single' ? { mode, email: email.trim(), phone: phone.trim(), name: name.trim() } : { mode, csv, fileName })
            }
          >
            {saving ? 'Adding…' : mode === 'single' ? 'Add creator' : 'Upload creators'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="inline-flex rounded-md border border-background-300 p-0.5" role="tablist">
          {(['single', 'csv'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={mode === option}
              onClick={() => setMode(option)}
              className={`cursor-pointer rounded px-3 py-1.5 text-xs font-label font-medium transition-colors ${
                mode === option ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {option === 'single' ? 'One creator' : 'Upload a CSV'}
            </button>
          ))}
        </div>

        {error ? (
          <p role="alert" className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5" />
            {error}
          </p>
        ) : null}

        {mode === 'single' ? (
          <>
            <div>
              <label htmlFor="add-email" className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
                Email
              </label>
              <input id="add-email" type="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="creator@example.com" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="add-name" className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
                  Name (optional)
                </label>
                <input id="add-name" className={inputClass} value={name} maxLength={255} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <label htmlFor="add-phone" className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
                  Phone (optional)
                </label>
                <input id="add-phone" type="tel" className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0712345678" />
              </div>
            </div>
            <p className="text-xs text-foreground-500">
              A creator removed earlier is re-invited. Anyone already on the campaign is refused.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-foreground-600">
              One creator per row, with the columns <code className="text-xs">name</code>, <code className="text-xs">email</code> and{' '}
              <code className="text-xs">phone</code>. Rows with neither an email nor a phone are skipped, and creators already on the
              campaign are left out.
            </p>
            <Button variant="outline" size="sm" icon="ri-download-2-line" onClick={downloadTemplate}>
              Download the template
            </Button>
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 px-4 py-6 text-center text-sm text-foreground-600 hover:bg-background-200">
              <i className="ri-upload-cloud-2-line text-xl text-foreground-400" />
              {fileName ? <span className="font-medium text-foreground-900">{fileName}</span> : 'Choose a .csv file'}
              <input type="file" accept=".csv,text/csv,.txt" className="sr-only" onChange={(e) => pickFile(e.target.files?.[0])} />
            </label>
            {fileError ? <p className="text-xs text-primary-700">{fileError}</p> : null}
          </>
        )}
      </div>
    </Modal>
  );
}
